<template>
  <section class="dp-layout fe-edu-page">
    <section class="dp-left">
      <DepartmentPane directory-scope="teaching" @tree-node-click="handleTreeNodeClick" />
    </section>

    <section class="dp-right fe-main">
      <header class="fe-page-header" :class="{ 'fe-page-header--stacked': selectedClassId }">
        <div class="fe-page-header__left">
          <div class="fe-page-header__icon fe-page-header__icon--indigo">
            <i class="el-icon-data-analysis"></i>
          </div>
          <div class="fe-page-header__text">
            <h2 class="fe-page-header__title">数据看板</h2>
            <p class="fe-page-header__sub">{{ selectedClassName || "请在左侧选择班级" }}</p>
          </div>
          <span
            v-if="classStatusText"
            class="fe-status-pill fe-page-header__status"
            :class="{
              'fe-status-pill--warning': classStatusType === 'warning',
              'fe-status-pill--success': classStatusType === 'success',
              'fe-status-pill--info': classStatusType === 'info',
            }"
          >{{ classStatusText }}</span>
        </div>
        <div v-if="selectedClassId" class="fe-page-header__toolbar">
          <FePeriodFilter
            :school-department-id="schoolDepartmentId"
            :period-type.sync="periodType"
            :period-key.sync="periodKey"
            @change="handlePeriodChange"
          />
          <el-input
            v-model="searchKeyword"
            placeholder="姓名 / 卡号"
            size="small"
            clearable
            prefix-icon="el-icon-search"
            class="fe-search-input"
            @keyup.enter.native="handleSearch"
            @clear="handleSearch"
          />
          <el-button
            type="primary"
            size="small"
            icon="el-icon-refresh"
            class="fe-btn-primary"
            @click="loadTableData"
          >刷新</el-button>
        </div>
      </header>

      <div v-if="!selectedClassId" class="fe-empty-state">
        <div class="fe-empty-state__icon">
          <i class="el-icon-school"></i>
        </div>
        <h3 class="fe-empty-state__title">选择班级查看成绩</h3>
        <p class="fe-empty-state__desc">在左侧部门树中点击班级，即可查看学生五育成绩</p>
      </div>

      <section v-else class="fe-card fe-card--fill">
        <div class="fe-card__head">
          <span class="fe-card__title">学生五育成绩</span>
          <span class="fe-card__tip">按座位号排序 · 综合星级德与智体美劳均完成后显示 · 一级星级请点击「查看」</span>
        </div>
        <div class="fe-card__body">
          <div class="tbl-scroll-host">
                    <el-table height="100%"
            v-loading="loading"
            :data="tableData"
            class="fe-table"
            style="width: 100%"
            :empty-text="tableEmptyHint"
          >
            <el-table-column label="座位号" :resizable="false" width="80" align="center">
              <template slot-scope="{ row }">{{ formatSeatNumber(row.SeatNumber) }}</template>
            </el-table-column>
            <el-table-column label="姓名" :resizable="false" prop="StudentName" min-width="120" show-overflow-tooltip />
            <el-table-column label="班级" :resizable="false" prop="ClassName" min-width="140" show-overflow-tooltip />
            <el-table-column label="卡号" :resizable="false" prop="CardNumber" min-width="140" show-overflow-tooltip />
            <el-table-column label="综合星级" :resizable="false" min-width="160" align="center">
              <template slot-scope="{ row }">
                <StarDisplay v-if="row.OverallDisplayStar != null" :value="row.OverallDisplayStar" :max="5" />
                <span v-else class="fe-star-empty">—</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" :resizable="false" width="80" align="center" fixed="right">
              <template slot-scope="{ row }">
                <el-button type="text" size="mini" class="fe-link-action" @click="openStudentDetail(row)">查看</el-button>
              </template>
            </el-table-column>
          </el-table>
          </div>
        </div>
        <div class="fe-card__foot">
          <el-pagination
            layout="total, prev, pager, next, jumper"
            :current-page.sync="pageIndex"
            :page-size.sync="pageSize"
            :page-sizes="[10, 20, 50]"
            :total="totalCount"
            @current-change="handlePageChange"
            @size-change="handleSizeChange"
          />
        </div>
      </section>
    </section>

    <el-drawer
      :title="currentStudentName + ' — 评价详情'"
      :visible.sync="drawerVisible"
      direction="rtl"
      size="480px"
      custom-class="fe-drawer"
    >
      <div class="fe-drawer-body" v-loading="detailLoading">
        <template v-if="studentDetail && isDetailReady">
          <div v-if="studentDetail.HasOpenSubjectRound" class="fe-detail-notice">
            {{ currentPeriodLabel || ("第 " + studentDetail.RoundNo + " 轮") }}汇总；德与智体美劳均完成后显示综合星级
          </div>
          <div class="fe-detail-toolbar">
            <div v-if="detailRoundTabs.length" class="fe-detail-round-tabs">
              <button
                v-for="tab in detailRoundTabs"
                :key="tab.roundNo"
                type="button"
                class="fe-detail-round-tab"
                :class="{ 'fe-detail-round-tab--active': detailRoundNo === tab.roundNo }"
                @click="switchDetailRound(tab.roundNo)"
              >{{ tab.label }}</button>
            </div>
            <button
              type="button"
              class="fe-detail-trend-entry"
              :disabled="!detailCategories.length"
              @click="openGrowthTrendModal"
            >
              成长趋势
              <i class="el-icon-arrow-right"></i>
            </button>
          </div>
          <div class="fe-detail-summary">
            <span class="fe-detail-summary__label">综合星级</span>
            <StarDisplay :value="studentDetail.OverallDisplayStar" :max="5" />
          </div>
          <div v-if="l1StarList.length" class="fe-l1-grid">
            <div v-for="cat in l1StarList" :key="cat.CategoryL1Id" class="fe-l1-item">
              <span class="fe-l1-item__name">{{ cat.CategoryName }}</span>
              <StarDisplay :value="cat.DisplayStar" :max="5" size="small" />
            </div>
          </div>
          <div v-else class="fe-no-detail">暂无一级星级数据</div>
        </template>
        <div v-else-if="studentDetail && studentDetail.StatusMessage" class="fe-no-detail">
          {{ studentDetail.StatusMessage }}
        </div>
        <div v-else-if="!detailLoading" class="fe-no-detail">
          {{ drawerEmptyHint }}
        </div>
      </div>
    </el-drawer>

    <FeGrowthTrendDialog
      :visible.sync="growthTrendModalVisible"
      :student-id="currentStudentId"
      :student-name="currentStudentName"
      :overall-star="studentDetail ? studentDetail.OverallDisplayStar : null"
      :categories="detailCategories"
    />
  </section>
</template>

<script>
import DepartmentPane from "@/components/DepartmentPane";
import StarDisplay from "@/components/StarDisplay";
import FeGrowthTrendDialog from "./FeGrowthTrendDialog.vue";
import FePeriodFilter from "./FePeriodFilter.vue";
import {
  getClassRatingStatusList,
  getClassRankingPage,
  getClassRoundHistory,
  getStudentStarDetail,
  getCurrentMonthPeriodKey,
  isClassDeptNode,
  resolveSchoolIdFromTreeNode,
} from "@/api/fiveEducation";
import { deptIdsEqual, treeNodeDeptId } from "@/utils/deptCascaderProps";
import "./fe-theme.css";
import "./fe-theme-global.css";

export default {
  name: "Dashboard",
  components: { DepartmentPane, StarDisplay, FeGrowthTrendDialog, FePeriodFilter },
  data() {
    return {
      schoolDepartmentId: null,
      selectedClassId: null,
      selectedClassName: "",
      periodType: "month",
      periodKey: getCurrentMonthPeriodKey(),
      currentPeriodLabel: "",
      classStatusText: "",
      classStatusType: "info",
      classSummaryStatus: "",
      finishedRoundCount: 0,
      searchKeyword: "",
      loading: false,
      tableData: [],
      totalCount: 0,
      pageIndex: 1,
      pageSize: 10,
      drawerVisible: false,
      currentStudentId: null,
      currentStudentName: "",
      detailLoading: false,
      studentDetail: null,
      l1StarList: [],
      detailRoundTabs: [],
      detailRoundNo: 0,
      detailCategories: [],
      growthTrendModalVisible: false,
    };
  },
  computed: {
    tableEmptyHint() {
      return "暂无学生数据";
    },
    drawerEmptyHint() {
      if (this.studentDetail && this.studentDetail.StatusMessage) {
        return this.studentDetail.StatusMessage;
      }
      if (this.classSummaryStatus === "notStarted") return "该周期尚未开启五育评价";
      if (this.classSummaryStatus === "inProgress") {
        return "评价进行中，德与智体美劳均完成后显示综合星级";
      }
      return "该周期尚无已结束的评价数据";
    },
    isDetailReady() {
      return !this.studentDetail
        || !this.studentDetail.SummaryStatus
        || this.studentDetail.SummaryStatus === "ready";
    },
  },
  methods: {
    /** 部门树节点点击：选中班级后直接加载看板 */
    async handleTreeNodeClick(data, node) {
      if (!data || !isClassDeptNode(node)) {
        this.resetScope();
        return;
      }

      this.schoolDepartmentId = resolveSchoolIdFromTreeNode(node, data.value);
      this.selectedClassId = treeNodeDeptId(data);
      this.selectedClassName = data.label || "";
      this.searchKeyword = "";
      this.pageIndex = 1;
      // 切换班级只改变范围，不重置周期筛选（由用户自行控制）
      await this.loadClassStatus();
      await this.loadTableData();
    },

    /** 周期筛选变更 */
    async handlePeriodChange() {
      if (!this.selectedClassId) return;
      this.pageIndex = 1;
      await this.loadClassStatus();
      await this.loadTableData();
    },

    resetScope() {
      this.selectedClassId = null;
      this.selectedClassName = "";
      this.classStatusText = "";
      this.classSummaryStatus = "";
      this.finishedRoundCount = 0;
      this.searchKeyword = "";
      this.tableData = [];
      this.totalCount = 0;
      this.pageIndex = 1;
    },

    /** 加载班级评价状态 */
    async loadClassStatus() {
      this.classStatusText = "";
      this.classSummaryStatus = "";
      this.finishedRoundCount = 0;
      if (!this.schoolDepartmentId || !this.selectedClassId) return;

      try {
        const res = await getClassRatingStatusList(
          this.schoolDepartmentId,
          this.periodType,
          this.periodKey
        );
        if (res.data && res.data.success) {
          const list = res.data.response || [];
          const row = list.find(function (r) {
            return deptIdsEqual(r.ClassDepartmentId, this.selectedClassId);
          }.bind(this));
          if (!row) {
            this.classStatusText = "未开启评价";
            this.classStatusType = "info";
            this.classSummaryStatus = "notStarted";
            this.currentPeriodLabel = "";
            return;
          }

          this.currentPeriodLabel = row.PeriodLabel || "";
          this.finishedRoundCount = Number(row.FinishedRoundCount) || 0;
          if (row.IsOpen) {
            this.classStatusText = (row.PeriodLabel || "") + " · 进行中";
            this.classStatusType = "warning";
            this.classSummaryStatus = this.finishedRoundCount > 0 ? "ready" : "inProgress";
          } else if (row.LatestRoundNo > 0) {
            this.classStatusText = (row.PeriodLabel || "") + " · 已结束";
            this.classStatusType = "success";
            this.classSummaryStatus = "ready";
          } else {
            this.classStatusText = (row.PeriodLabel || "") + " · 未开启";
            this.classStatusType = "info";
            this.classSummaryStatus = "notStarted";
          }
        }
      } catch (e) {
        // 状态加载失败不阻断主列表
      }
    },

    handleSearch() {
      this.pageIndex = 1;
      this.loadTableData();
    },

    handlePageChange(page) {
      this.pageIndex = page;
      this.loadTableData();
    },

    handleSizeChange(size) {
      this.pageSize = size;
      this.pageIndex = 1;
      this.loadTableData();
    },

    /** 加载班级排行榜（接口分页） */
    async loadTableData() {
      if (!this.selectedClassId) return;
      this.loading = true;
      try {
        const res = await getClassRankingPage(
          this.selectedClassId,
          0,
          this.pageIndex,
          this.pageSize,
          this.searchKeyword.trim(),
          this.periodType,
          this.periodKey
        );
        if (res.data && res.data.success) {
          const page = res.data.response || {};
          this.tableData = page.data || [];
          this.totalCount = page.dataCount != null ? page.dataCount : 0;
          this.pageIndex = page.page || this.pageIndex;
        } else {
          this.tableData = [];
          this.totalCount = 0;
        }
      } finally {
        this.loading = false;
      }
    },

    /** 格式化座位号展示 */
    formatSeatNumber(seatNumber) {
      var num = Number(seatNumber);
      if (!num || num <= 0) return "—";
      return num < 10 ? "0" + num : String(num);
    },

    /** 打开详情：轮次切换 + 一级星级 + 成长趋势 */
    async openStudentDetail(row) {
      this.currentStudentId = row.StudentId;
      this.currentStudentName = row.StudentName || "";
      this.studentDetail = null;
      this.l1StarList = [];
      this.detailRoundTabs = [];
      this.detailRoundNo = 0;
      this.detailCategories = [];
      this.growthTrendModalVisible = false;
      this.drawerVisible = true;
      this.detailLoading = true;
      try {
        await this.loadDetailRoundTabs();
        if (this.detailRoundNo > 0) {
          await this.loadStudentDetail(this.detailRoundNo);
        } else {
          await this.loadStudentDetail(0);
        }
      } finally {
        this.detailLoading = false;
      }
    },

    /** 加载抽屉内「本次/上次」Tab（最近两轮已结束） */
    async loadDetailRoundTabs() {
      this.detailRoundTabs = [];
      if (!this.selectedClassId) return;

      try {
        const res = await getClassRoundHistory(this.selectedClassId);
        if (!res.data || !res.data.success) return;

        const closedRounds = (res.data.response || [])
          .filter(function (r) {
            return r.ClosedAt && !r.IsOpen;
          })
          .sort(function (a, b) {
            return Number(b.RoundNo) - Number(a.RoundNo);
          });

        const tabLabels = ["本次", "上次"];
        this.detailRoundTabs = closedRounds.slice(0, 2).map(function (r, index) {
          return {
            roundNo: Number(r.RoundNo),
            label: r.PeriodLabel || tabLabels[index] || ("第" + r.RoundNo + "轮"),
          };
        });

        if (this.detailRoundTabs.length) {
          this.detailRoundNo = this.detailRoundTabs[0].roundNo;
        } else {
          this.detailRoundNo = 0;
        }
      } catch (e) {
        this.detailRoundNo = 0;
      }
    },

    /** 加载学生详情（roundNo=0 时按班级状态自动分支） */
    async loadStudentDetail(roundNo) {
      if (!this.currentStudentId) return;
      var queryRound = roundNo != null ? roundNo : this.detailRoundNo;
      this.detailLoading = true;
      try {
        const res = await getStudentStarDetail(this.currentStudentId, queryRound);
        if (res.data && res.data.success) {
          this.studentDetail = res.data.response;
          if (!this.isDetailReady) {
            this.detailCategories = [];
            this.l1StarList = [];
            return;
          }
          this.detailCategories = (this.studentDetail && this.studentDetail.Categories)
            ? this.studentDetail.Categories
            : [];
          this.l1StarList = this.detailCategories.map(function (c) {
            return {
              CategoryL1Id: c.CategoryL1Id,
              CategoryName: c.CategoryName,
              DisplayStar: c.DisplayStar,
            };
          });
        }
      } finally {
        this.detailLoading = false;
      }
    },

    /** 切换本次/上次 */
    async switchDetailRound(roundNo) {
      if (this.detailRoundNo === roundNo) return;
      this.detailRoundNo = roundNo;
      this.growthTrendModalVisible = false;
      await this.loadStudentDetail(roundNo);
    },

    /** 打开成长趋势弹窗（折线图，Y 轴为星级） */
    openGrowthTrendModal() {
      if (!this.detailCategories.length) return;
      this.growthTrendModalVisible = true;
    },
  },
};
</script>

<style scoped>
.fe-page-header__left {
  flex-wrap: nowrap;
}
.fe-page-header__status {
  flex-shrink: 0;
  white-space: nowrap;
}
.fe-page-header__toolbar .fe-search-input {
  width: 180px;
  flex-shrink: 0;
}
.fe-edu-page .fe-main {
  overflow: hidden;
}
.fe-star-empty {
  color: #9ca3af;
  font-size: 14px;
}
.fe-detail-notice {
  margin-bottom: 12px;
  padding: 8px 12px;
  font-size: 12px;
  line-height: 1.5;
  color: #b45309;
  background: #fffbeb;
  border-radius: 8px;
  border: 1px solid #fde68a;
}
</style>
